// src/components/auth/CreateAccountModal.jsx
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { EyeOpen, EyeClosed } from '../../public/icons';
import { useAuth } from '../../context/AuthContext';
import GoogleSignInButton from './GoogleSignInButton';

const CreateAccountModal = ({ onClose, onSwitchToSignIn, redirectTo }) => {
  const { register, loading, error, clearMessages } = useAuth();
  const navigate = useNavigate();
  const [showPassword, setShowPassword] = useState(false);
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    password: '',
    updates: false,
  });
  const [localError, setLocalError] = useState('');
  const [validationErrors, setValidationErrors] = useState({});

  const validateForm = () => {
    const errors = {};

    if (!formData.firstName.trim()) errors.firstName = 'First name is required';
    if (!formData.lastName.trim()) errors.lastName = 'Last name is required';

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!formData.email.trim()) {
      errors.email = 'Email is required';
    } else if (!emailRegex.test(formData.email)) {
      errors.email = 'Please enter a valid email';
    }

    if (!formData.password) {
      errors.password = 'Password is required';
    } else if (formData.password.length < 6) {
      errors.password = 'Password must be at least 6 characters';
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));

    // Clear validation error for this field
    if (validationErrors[name]) {
      setValidationErrors((prev) => ({
        ...prev,
        [name]: '',
      }));
    }

    // Clear messages when user starts typing
    if (error || localError) {
      clearMessages();
      setLocalError('');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      setLocalError('Please fix the errors in the form');
      return;
    }

    try {
      const result = await register(formData);

      if (result.success) {
        // ✅ Redirect back to where the user came from (e.g. /checkout)
        navigate(redirectTo || '/account');

        setTimeout(() => {
          onClose();
        }, 1500);
      } else {
        setLocalError(result.message);
      }
    } catch (err) {
      setLocalError(err.message || 'Registration failed');
    }
  };

  // Loading spinner component
  const LoadingSpinner = () => (
    <div className="flex items-center justify-center">
      <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
      <span className="ml-2">Processing...</span>
    </div>
  );

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[999]">
      <div className="bg-[#FCF7EE] w-[516px] overflow-hidden relative">
        {/* ❌ Close (X) button */}
      <div className=' flex justify-end p-4'>  <button
          type="button"
          onClick={onClose}
          disabled={loading}
          aria-label="Close"
          className="0 w-9 h-9 flex items-center justify-center rounded-sm border border-[#DFDFDF] text-[#1C2A46] 0 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="w-5 h-5"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            strokeWidth={2}
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M6 18L18 6M6 6l12 12"
            />
          </svg>
        </button></div>

        <div className="px-3 ">
          {/* Error Messages */}
          {(error || localError) && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 text-red-700 rounded-md">
              <div className="flex items-center">
                <svg
                  className="w-5 h-5 mr-2"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path
                    fillRule="evenodd"
                    d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                    clipRule="evenodd"
                  />
                </svg>
                <span className="text-sm">{error || localError}</span>
              </div>
            </div>
          )}

          {/* Tabs */}
          <div className="flex border-b-2 border-[#DFDFDF] mb-6 mt-2">
            <button
              onClick={() => {
                clearMessages();
                onSwitchToSignIn();
              }}
              className="flex-1 pb-3 text-center text-lg text-secondary font-normal hover:text-primary transition-colors"
              disabled={loading}
            >
              Sign In
            </button>
            <button className="flex-1 pb-3 text-center text-lg text-secondary font-normal border-b-2 border-secondary">
              Create Account
            </button>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit}>
            <h1 className="text-[#1C2A46] text-xl font-medium mb-2.5">
              Create your Kapta account
            </h1>
            <div className="space-y-5">
              {/* Name Fields */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <input
                    type="text"
                    name="firstName"
                    value={formData.firstName}
                    onChange={handleChange}
                    className={`w-full p-4 border bg-[#E4E4E4] placeholder:text-[#6B635D] placeholder:text-sm placeholder:font-light focus:outline-none focus:ring-2 focus:ring-primary ${
                      validationErrors.firstName
                        ? 'border-red-500'
                        : 'border-gray-300'
                    }`}
                    placeholder="* Enter first name"
                    disabled={loading}
                  />
                  {validationErrors.firstName && (
                    <p className="mt-1 text-xs text-red-500">
                      {validationErrors.firstName}
                    </p>
                  )}
                </div>
                <div>
                  <input
                    type="text"
                    name="lastName"
                    value={formData.lastName}
                    onChange={handleChange}
                    className={`w-full p-4 border bg-[#E4E4E4] placeholder:text-[#6B635D] placeholder:text-sm placeholder:font-light focus:outline-none focus:ring-2 focus:ring-primary ${
                      validationErrors.lastName
                        ? 'border-red-500'
                        : 'border-gray-300'
                    }`}
                    placeholder="* Enter last name"
                    disabled={loading}
                  />
                  {validationErrors.lastName && (
                    <p className="mt-1 text-xs text-red-500">
                      {validationErrors.lastName}
                    </p>
                  )}
                </div>
              </div>

              {/* Email */}
              <div>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  className={`w-full p-4 border bg-[#E4E4E4] placeholder:text-[#6B635D] placeholder:text-sm placeholder:font-light focus:outline-none focus:ring-2 focus:ring-primary ${
                    validationErrors.email ? 'border-red-500' : 'border-gray-300'
                  }`}
                  placeholder="Enter your email"
                  disabled={loading}
                />
                {validationErrors.email && (
                  <p className="mt-1 text-xs text-red-500">
                    {validationErrors.email}
                  </p>
                )}
              </div>

              {/* Password */}
              <div>
                <div className="relative">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    name="password"
                    value={formData.password}
                    onChange={handleChange}
                    className={`w-full p-4 border bg-[#E4E4E4] placeholder:text-[#6B635D] placeholder:text-sm placeholder:font-light focus:outline-none focus:ring-2 focus:ring-primary pr-10 ${
                      validationErrors.password
                        ? 'border-red-500'
                        : 'border-gray-300'
                    }`}
                    placeholder="Enter your password"
                    disabled={loading}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-gray-700"
                    disabled={loading}
                  >
                    {showPassword ? (
                      <EyeClosed className="w-5 h-5" />
                    ) : (
                      <EyeOpen className="w-5 h-5" />
                    )}
                  </button>
                </div>
                {validationErrors.password && (
                  <p className="mt-1 text-xs text-red-500">
                    {validationErrors.password}
                  </p>
                )}
                <p className="mt-1 text-xs text-gray-500">
                  Password must be at least 6 characters
                </p>
              </div>

              {/* Checkbox */}
              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="updates"
                  name="updates"
                  checked={formData.updates}
                  onChange={handleChange}
                  className="w-5 h-5 rounded border-gray-300 text-primary focus:ring-primary"
                  disabled={loading}
                />
                <label
                  htmlFor="updates"
                  className="ml-2 text-xs text-[#1C2A46] font-light"
                >
                  Keep me updated about new Kapta products and stories
                </label>
              </div>

              {/* Submit Button */}
              <button
                type="submit"
                disabled={loading}
                className={`w-full bg-primary text-[#FCF7EE] py-3 text-base font-medium transition-colors flex items-center justify-center ${
                  loading ? 'opacity-75 cursor-not-allowed' : 'hover:bg-opacity-90'
                }`}
              >
                {loading ? <LoadingSpinner /> : 'Create account'}
              </button>
            </div>
          </form>

          {/* Divider */}
          <div className="flex text-left my-3">
            <span className="text-[#6B635D] font-normal text-sm">
              Or, Sign up with
            </span>
          </div>

          {/* ✅ Social Sign Up — Google (respects redirectTo) */}
          <div className="space-y-3">
            <GoogleSignInButton
              buttonText="Sign up with Google"
              disabled={loading}
              redirectTo={redirectTo}
              onSuccess={() => {
                onClose();
              }}
              onError={(msg) => setLocalError(msg)}
            />
          </div>

          {/* Footer Text */}
          <p className="mt-1 mb-8 text-left font-light text-xs text-[#151515]">
            By creating an account, your memories and products are securely
            saved and accessible anytime.
          </p>
        </div>
      </div>
    </div>
  );
};

export default CreateAccountModal;